import React, { useEffect, useRef } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import PatientSessionProvider from 'infrastructure/providers/patient-session-provider';
import { endActiveContact } from 'services/connectService';
import { useAppDispatch, useAppSelector } from 'store';

/**
 * The layout route shared by the patient chart, the unified profile and the
 * Admin Panel. React Router keeps this element mounted while the agent moves
 * between those pages — the same reason PatientSessionProvider can accumulate
 * "pages accessed" across the switch — and unmounts it only when they leave the
 * patient. That makes it the one place that can tell "went to the other page"
 * apart from "left this patient", so it is where the call window is put down.
 *
 * Closing the window used to be each page's own unmount cleanup, which cannot
 * draw that distinction: every Close Chart AND every Admin Panel ⇄ chart switch
 * looked identical to it, so the window closed under agents who were still
 * mid-wrap-up. Hoisted here it closes when the patient does — Close Chart, or a
 * jump to another patient — and survives the page switch.
 *
 * It never ends after-call work. endActiveContact only cancels a ring that is
 * still connecting; an ended contact is left open, because closing it is
 * CLEAR_CONTACT and that is the agent's own Close contact button on the call
 * dashboard, never a side effect of navigating.
 */
const PatientSessionLayout: React.FC = () => {
  const { patientGuid } = useParams<{ patientGuid: string }>();

  const { handleConnectPopoutOpen } = useAppDispatch().connect;
  const connectPopoutOpen = useAppSelector(
    (state) => state.connect.connectPopoutOpen,
  );
  // Read through a ref so the effect can depend on the patient alone. With the
  // open flag in the dependency list, opening the window would re-run the
  // effect and its cleanup would close it again on the patient the agent is
  // still sitting on.
  const isPopoutOpen = useRef(connectPopoutOpen);
  isPopoutOpen.current = connectPopoutOpen;

  useEffect(
    () => () => {
      if (!isPopoutOpen.current) return;
      endActiveContact();
      handleConnectPopoutOpen(false);
    },
    [patientGuid],
  );

  return (
    <PatientSessionProvider patientGuid={patientGuid!}>
      <Outlet />
    </PatientSessionProvider>
  );
};

export default PatientSessionLayout;
